import React from 'react';
import Icon from '../../../components/AppIcon';

const TrailContent = ({ trailData }) => {
  const {
    name,
    description,
    historicalSignificance,
    naturalFeatures,
    culturalAttractions,
    practicalInfo,
    safetyTips
  } = trailData;

  const contentSections = [
    {
      id: 'overview',
      title: 'Trail Overview',
      icon: 'FileText',
      content: description,
      color: 'text-primary'
    },
    {
      id: 'historical',
      title: 'Historical Significance',
      icon: 'Clock',
      content: historicalSignificance,
      color: 'text-accent'
    },
    {
      id: 'natural',
      title: 'Natural Features',
      icon: 'Trees',
      content: naturalFeatures,
      color: 'text-success'
    },
    {
      id: 'cultural',
      title: 'Cultural Attractions',
      icon: 'Building2',
      content: culturalAttractions,
      color: 'text-warning'
    },
    {
      id: 'practical',
      title: 'Practical Information',
      icon: 'Info',
      content: practicalInfo,
      color: 'text-muted-foreground'
    },
    {
      id: 'safety',
      title: 'Safety & Guidelines',
      icon: 'Shield',
      content: safetyTips,
      color: 'text-destructive'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Trail Title */}
      <div className="text-center lg:text-left">
        <h1 className="font-heading font-bold text-3xl lg:text-4xl text-foreground mb-4">
          {name}
        </h1>
        <div className="flex items-center justify-center lg:justify-start gap-2 text-muted-foreground">
          <Icon name="MapPin" size={16} />
          <span className="text-sm">Heritage Trail Experience</span>
        </div>
      </div>
      {/* Content Sections */}
      <div className="space-y-8">
        {contentSections?.map((section) => (
          <div key={section?.id} className="bg-card rounded-lg shadow-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-muted">
                <Icon name={section?.icon} size={20} className={section?.color} />
              </div>
              <h2 className="font-heading font-semibold text-xl text-foreground">
                {section?.title}
              </h2>
            </div>
            
            <div className="prose prose-sm max-w-none">
              {Array.isArray(section?.content) ? (
                <div className="space-y-4">
                  {section?.content?.map((paragraph, index) => (
                    <p key={index} className="text-muted-foreground leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground leading-relaxed">
                  {section?.content}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Quick Facts */}
      <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-6">
        <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
          <Icon name="Lightbulb" size={20} className="text-primary" />
          Did You Know?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <div className="p-1 rounded-full bg-accent/20 mt-1">
              <Icon name="Star" size={12} className="text-accent" />
            </div>
            <p className="text-sm text-muted-foreground">
              This trail connects over 15 heritage sites spanning both Serbia and Montenegro.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-1 rounded-full bg-success/20 mt-1">
              <Icon name="Users" size={12} className="text-success" />
            </div>
            <p className="text-sm text-muted-foreground">
              Over 10,000 visitors explore this route annually, making it one of the most popular heritage trails.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-1 rounded-full bg-warning/20 mt-1">
              <Icon name="Calendar" size={12} className="text-warning" />
            </div>
            <p className="text-sm text-muted-foreground">
              The trail features sites dating back over 800 years, showcasing medieval architecture and traditions.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-1 rounded-full bg-primary/20 mt-1">
              <Icon name="Award" size={12} className="text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              Recognized by UNESCO for its outstanding cultural and natural heritage preservation efforts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrailContent;